import { useState } from 'react'
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { WizardData, WizardDepartment } from './types'

interface StepDepartmentsProps {
  data: WizardData
  onChange: (data: WizardData) => void
}

interface DeptForm {
  department_id: string
  name: string
  code: string
  description: string
  subDepts: string
}

const emptyForm: DeptForm = { department_id: '', name: '', code: '', description: '', subDepts: '' }

export function StepDepartments({ data, onChange }: StepDepartmentsProps) {
  const [showForm, setShowForm] = useState(false)
  const [editIdx, setEditIdx] = useState<number | null>(null)
  const [form, setForm] = useState<DeptForm>(emptyForm)

  const startAdd = () => {
    setForm(emptyForm)
    setEditIdx(null)
    setShowForm(true)
  }

  const startEdit = (idx: number) => {
    const d = data.departments[idx]
    setForm({
      department_id: d.department_id,
      name: d.name,
      code: d.code,
      description: d.description,
      subDepts: d.sub_departments.map((s) => s.name).join(', '),
    })
    setEditIdx(idx)
    setShowForm(true)
  }

  const save = () => {
    if (!form.name.trim()) return
    const subNames = form.subDepts
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)

    const dept: WizardDepartment = {
      ...(editIdx !== null ? data.departments[editIdx] : {}),
      department_id: form.department_id || form.name.toLowerCase().replace(/\s+/g, '_'),
      name: form.name,
      code: form.code.toUpperCase() || form.name.slice(0, 2).toUpperCase(),
      description: form.description,
      sub_departments: subNames.map((name, i) => {
        const existing =
          editIdx !== null
            ? data.departments[editIdx].sub_departments.find((s) => s.name === name)
            : undefined
        return (
          existing || {
            sub_department_id: name.toLowerCase().replace(/\s+/g, '_'),
            name,
            keywords: [],
            default_priority: 'normal',
            description: '',
            is_default: i === subNames.length - 1,
          }
        )
      }),
    }

    const depts = [...data.departments]
    if (editIdx !== null) {
      depts[editIdx] = dept
    } else {
      depts.push(dept)
    }
    onChange({ ...data, departments: depts })
    setShowForm(false)
  }

  const remove = (idx: number) => {
    onChange({ ...data, departments: data.departments.filter((_, i) => i !== idx) })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">Departments</h3>
          <p className="text-sm text-muted-foreground">
            Configure routing departments and sub-departments for service requests.
          </p>
        </div>
        {data.departments.length > 0 && !showForm && (
          <Button size="sm" onClick={startAdd}>
            <Plus className="h-4 w-4 mr-1" /> Add Department
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm font-semibold">
              {editIdx !== null ? 'Edit' : 'New'} Department
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Public Works"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>ID</Label>
                  <Input
                    value={form.department_id}
                    onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                    placeholder="public_works"
                    disabled={editIdx !== null}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code</Label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="PW"
                    maxLength={5}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of what this department handles"
              />
            </div>
            <div className="space-y-2">
              <Label>Sub-departments</Label>
              <Input
                value={form.subDepts}
                onChange={(e) => setForm({ ...form, subDepts: e.target.value })}
                placeholder="Comma-separated: Streets, Sidewalks, Drainage"
              />
              <p className="text-xs text-muted-foreground">
                Optional — add sub-departments separated by commas
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={!form.name.trim()}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {data.departments.length === 0 && !showForm ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <Building2 className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm font-medium">No departments yet</p>
            <p className="text-xs text-muted-foreground text-center max-w-xs">
              Departments define how service requests get routed to the right teams.
            </p>
            <Button size="sm" onClick={startAdd}>
              <Plus className="h-4 w-4 mr-1" /> Add First Department
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.departments.map((dept, i) => (
            <Card key={i}>
              <CardContent className="pt-6 flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="rounded-lg bg-primary/10 p-2 mt-0.5">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{dept.name}</span>
                      <Badge variant="outline" className="font-mono text-xs">
                        {dept.code}
                      </Badge>
                    </div>
                    {dept.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">{dept.description}</p>
                    )}
                    {dept.sub_departments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {dept.sub_departments.map((s, j) => (
                          <Badge key={j} variant="secondary" className="text-xs">
                            {s.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(i)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(i)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
